import { FC, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotify, useRefresh } from "react-admin";
import axiosInstance from "@/utils/axiosInstance";
import { toast } from "sonner";

interface Props {
  url: string;
  id: number;
  vehicleId?: number | null;
  vehicleList: any[];
}

const AssignVehicle: FC<Props> = ({
  url,
  id,
  vehicleList: vehicles,
  vehicleId,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<string>("");
  const [vehicleAlreadyAssigned, setVehicleAlreadyAssigned] =
    useState<boolean>(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleValueChange = async (val: string) => {
    try {
      if (url === "devices") {
        notify(`Checking if vehicle is already assigned...`, { type: "info" });

        // check currently assigned devices to this vehicle
        const res = await axiosInstance.get(`devices/vehicle/${val}/assigned`);
        const vehicleAssigned =
          res.data && res.data.find((d: any) => d.id !== id);
        setVehicleAlreadyAssigned(vehicleAssigned ? true : false);
      }
      setSelectedVehicle(val);
      setOpen(true);
    } catch (error) {
      console.error(error);
      setSelectedVehicle("");
      setVehicleAlreadyAssigned(false);
      refresh();
      notify(`Failed to check if vehicle is already assigned.`, {
        type: "error",
      });
      toast(error.response.data.message)
    }
  };

  const assignVehicle = async () => {
    try {
        await axiosInstance.put(`/${url}/${id}/assign-vehicle`, { vehicle_id: parseInt(selectedVehicle, 10) });
        notify(`The vehicle has been assigned successfully.`, { type: 'success' });
    } catch (error) {
        console.error(error);
        notify(`Failed to assign vehicle.`, { type: 'error' });
    }

    setOpen(false);
    setVehicleAlreadyAssigned(false);
    setSelectedVehicle("");
    refresh();
  };

  const handleCancelConfirmation = () => {
    setOpen(false);
    setVehicleAlreadyAssigned(false);
    setSelectedVehicle("");
    refresh();
  };

  return (
    <>
      <Select value={vehicleId?.toString()} onValueChange={handleValueChange}>
        <SelectTrigger className="h-8 w-40">
          <SelectValue placeholder="Assign vehicle" />
        </SelectTrigger>
        <SelectContent>
          {vehicles?.map((item) => (
            <SelectItem key={item.id} value={item.id.toString()}>
              {item.nickname}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Confirmation Dialog for Already Assigned Vehicle */}
      <Dialog
        open={open}
        onOpenChange={(open) => !open && handleCancelConfirmation()}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {vehicleAlreadyAssigned
                ? "Vehicle Already Assigned"
                : "Confirm Vehicle Assignment"}
            </DialogTitle>
            <DialogDescription>
              {vehicleAlreadyAssigned
                ? "The vehicle chosen has already another device assigned and this will remove that other device. Are you sure you want to proceed?"
                : "Are you sure you want to assign this vehicle? You can change it later."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelConfirmation}>
              Cancel
            </Button>
            <Button onClick={assignVehicle}>Confirm Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssignVehicle;
